import type { ComplianceIssue, Severity } from './types.js';
import { publicRegulationIds } from './regulation-labels.js';

/**
 * A single row in the "Product issues" table. Multiple findings on the same
 * product collapse into one group — merchants think per-product, not
 * per-finding. Highest-severity + max-risk bubble up so the row represents
 * the worst of the product's issues.
 */
export interface ProductIssueGroup {
  productId: string;
  productTitle: string;
  locked: boolean;
  /** Max AI score across findings in this group. */
  maxScore: number;
  /** Highest severity across findings (critical > warning > info). */
  severity: Severity;
  /** Distinct content types flagged (description, image, meta, title). */
  contentTypes: ComplianceIssue['contentType'][];
  /** Union of *public* regulation ids — internal-only ids are dropped. */
  regulations: string[];
  /** Total number of underlying findings (for the detail page). */
  findingCount: number;
  /**
   * Info-severity findings *subordinate* to this group's main severity.
   * When the group is critical/warning, these info findings would otherwise
   * vanish silently — the UI surfaces them as a "+N informational" hint so
   * the row count reconciles with the scan summary.
   * When the group itself is info-severity, this is 0 (the row already
   * represents the info findings directly).
   */
  subordinateInfoCount: number;
  /** True when ALL findings in this group have fixApplied === true. */
  allFixed: boolean;
  /** A representative finding id; used as the click target on paid tiers. */
  representativeFindingId: string;
}

const SEVERITY_RANK: Record<Severity, number> = { critical: 3, warning: 2, info: 1 };

export function groupFindingsByProduct(findings: ComplianceIssue[]): ProductIssueGroup[] {
  const byProduct = new Map<string, ProductIssueGroup>();
  // Track raw info-finding counts per product; resolved to
  // `subordinateInfoCount` after we know each group's final severity.
  const infoCountByProduct = new Map<string, number>();

  for (const f of findings) {
    if (f.severity === 'info') {
      infoCountByProduct.set(f.productId, (infoCountByProduct.get(f.productId) ?? 0) + 1);
    }

    const existing = byProduct.get(f.productId);
    if (!existing) {
      byProduct.set(f.productId, {
        productId: f.productId,
        productTitle: f.productTitle,
        locked: Boolean(f.locked),
        maxScore: f.aiScore,
        severity: f.severity,
        contentTypes: [f.contentType],
        regulations: publicRegulationIds(f.regulations),
        findingCount: 1,
        subordinateInfoCount: 0,
        allFixed: f.fixApplied,
        representativeFindingId: f.id,
      });
      continue;
    }

    existing.findingCount += 1;
    if (!f.fixApplied) existing.allFixed = false;
    if (f.aiScore > existing.maxScore) {
      existing.maxScore = f.aiScore;
      // Prefer the highest-risk finding as the click target.
      existing.representativeFindingId = f.id;
    }
    if (SEVERITY_RANK[f.severity] > SEVERITY_RANK[existing.severity]) {
      existing.severity = f.severity;
    }
    if (!existing.contentTypes.includes(f.contentType)) {
      existing.contentTypes.push(f.contentType);
    }
    for (const r of publicRegulationIds(f.regulations)) {
      if (!existing.regulations.includes(r)) existing.regulations.push(r);
    }
  }

  // Resolve subordinateInfoCount now that each group's severity is settled.
  // If the group itself is info-severity, the info findings *are* the row;
  // don't double-count them as subordinate.
  for (const g of byProduct.values()) {
    if (g.severity === 'info') continue;
    g.subordinateInfoCount = infoCountByProduct.get(g.productId) ?? 0;
  }

  // Order: severity desc, then score desc, then title asc (stable for UI).
  return Array.from(byProduct.values()).sort((a, b) => {
    const bySeverity = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (bySeverity !== 0) return bySeverity;
    if (b.maxScore !== a.maxScore) return b.maxScore - a.maxScore;
    return a.productTitle.localeCompare(b.productTitle);
  });
}
